<script setup lang="ts">
import axios from 'axios'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStorage } from '@vueuse/core'
import { useDisplay } from 'vuetify'
import ReportParameterFields from './ReportParameterFields.vue'
import ReportPreviewPanel from './ReportPreviewPanel.vue'
import { getApiError } from '@/composables/getApiError'
import { getBlobErrorMessage, parseContentDispositionFilename, saveBlobAsFile } from '@/modules/documents-new/utils/fileHelpers'
import { generateReport, generateReportDraft, prepareReport, previewReport, saveReportDraft } from '@/modules/reportTemplates/api'
import type { PreparedReport, ReportDraft, ReportFormat, ReportParameterValues, ReportPreview, ReportSource, ReportTemplate } from '@/modules/reportTemplates/api'
import { copiedParameters, parameterDefaults, validateReportParameters } from '@/modules/reportTemplates/parameters'
import { createPreviewScheduler } from '@/modules/reportTemplates/preview'

const props = defineProps<{ template: ReportTemplate; source: ReportSource; format: ReportFormat; draft?: ReportDraft; copy?: boolean; previewOnOpen?: boolean }>()
const emit = defineEmits<{ close: []; saved: [] }>()
const prepared = ref<PreparedReport | null>(null)
const values = ref<ReportParameterValues>({})
const savedDraft = ref<ReportDraft | undefined>(props.copy ? undefined : props.draft)
const title = ref(props.copy ? `${props.draft?.title || props.template.name} — копия`.slice(0, 200) : props.draft?.title || props.template.name)
const errors = ref<Record<string, string>>({})
const error = ref('')
const notice = ref('')
const loading = ref(true)
const busy = ref(false)
const step = ref(0)
const format = ref<ReportFormat>(props.format)
const labels = { pdf: 'PDF', docx: 'Word', xlsx: 'Excel' }
const closeConfirm = ref(false)
const { smAndDown } = useDisplay()
// Store only a display preference; document data stays in memory until explicitly saved.
const previewVisible = useStorage('report-preview-visible', true)
const mobileTab = ref<'fields' | 'preview'>('fields')
const preview = ref<ReportPreview | null>(null)
const previewLoading = ref(false)
const previewError = ref('')
const usesDraft = computed(() => Boolean(props.draft) || Boolean(prepared.value?.parameters.length))
const previewSource = computed(() => savedDraft.value?.source || prepared.value?.source)
const previewActive = computed(() => Boolean(prepared.value) && previewVisible.value && (!smAndDown.value || mobileTab.value === 'preview'))
const previewScheduler = createPreviewScheduler({
  load: (input: { source: ReportSource; format: ReportFormat; parameters: ReportParameterValues }, signal) =>
    previewReport(props.template.id, input.source, input.format, input.parameters, signal),
  pending: () => { previewLoading.value = true; previewError.value = '' },
  resolved: result => { preview.value = result; previewLoading.value = false },
  rejected: cause => {
    preview.value = null
    previewLoading.value = false
    previewError.value = getApiError(cause, 'Не удалось обновить предпросмотр. Проверьте введённые значения или повторите запрос.')
  },
})
function updatePreview() {
  if (!previewActive.value || !previewSource.value) {
    previewScheduler.cancel()
    preview.value = null
    previewLoading.value = false
    previewError.value = ''
    return
  }
  previewScheduler.schedule(JSON.parse(JSON.stringify({ source: previewSource.value, format: format.value, parameters: values.value })))
}
watch([previewActive, previewSource, values, format], updatePreview, { deep: true })
watch(previewVisible, visible => { if (!visible) mobileTab.value = 'fields' })
watch(format, () => { if (!prepared.value) void prepare() })
let baseline = ''
let request: AbortController | undefined
const groups = computed(() => [...new Set(prepared.value?.parameters.map(field => field.group) || [])])
const visibleGroups = computed(() => props.template.wizard ? groups.value.slice(step.value, step.value + 1) : groups.value)
const snapshot = () => JSON.stringify([title.value, format.value, values.value])
function close() {
  if (busy.value) return
  if (usesDraft.value && prepared.value && baseline !== snapshot()) closeConfirm.value = true
  else emit('close')
}
async function prepare() {
  request?.abort()
  const controller = new AbortController()
  request = controller
  loading.value = true
  error.value = ''
  try {
    const result = await prepareReport(props.template.id, props.source, format.value, controller.signal, props.draft?.parameters)
    if (!controller.signal.aborted) {
      prepared.value = result
      const initial = props.draft ? props.copy ? copiedParameters(result.parameters, props.draft.parameters) : props.draft.parameters : {}
      values.value = { ...parameterDefaults(result.parameters), ...JSON.parse(JSON.stringify(initial)) }
      baseline = snapshot()
    }
  } catch (cause) {
    if (!controller.signal.aborted) error.value = getApiError(cause, 'Не удалось подготовить форму')
  } finally {
    if (!controller.signal.aborted) loading.value = false
  }
}
function showFieldErrors() {
  mobileTab.value = 'fields'
  const group = prepared.value?.parameters.find(field => Object.keys(errors.value).some(key => key === field.key || key.startsWith(`${field.key}.`)))?.group
  if (group) step.value = groups.value.indexOf(group)
}
async function perform(download: boolean) {
  if (!prepared.value || busy.value) return
  error.value = ''
  notice.value = ''
  errors.value = download ? validateReportParameters(prepared.value.parameters, values.value) : {}
  if (usesDraft.value && !title.value.trim()) error.value = 'Введите название документа.'
  if (Object.keys(errors.value).length) {
    error.value = 'Проверьте отмеченные поля.'
    showFieldErrors()
  }
  if (error.value) return
  const controller = new AbortController()
  request = controller
  busy.value = true
  try {
    if (download && !usesDraft.value) {
      const response = await generateReport(props.template.id, prepared.value.source, format.value, controller.signal)
      if (controller.signal.aborted) return
      saveBlobAsFile(response.data, parseContentDispositionFilename(response.headers['content-disposition']) || `${props.template.name}.${format.value}`)
      emit('close')
      return
    }
    const draft = await saveReportDraft({
      title: title.value,
      template: props.template.id,
      source: savedDraft.value?.source || prepared.value.source,
      format: format.value,
      parameters: values.value,
      revision: savedDraft.value?.revision,
    }, savedDraft.value?.id, controller.signal)
    if (controller.signal.aborted) return
    savedDraft.value = draft
    baseline = snapshot()
    notice.value = 'Черновик сохранён в «Моих заявлениях».'
    emit('saved')
    if (download) {
      const response = await generateReportDraft(draft, controller.signal)
      if (controller.signal.aborted) return
      // Successful generation always increments the revision once; no CORS header dependency.
      savedDraft.value = { ...draft, revision: draft.revision + 1 }
      saveBlobAsFile(response.data, parseContentDispositionFilename(response.headers['content-disposition']) || `${props.template.name}.${format.value}`)
      emit('saved')
      emit('close')
    }
  } catch (cause) {
    if (controller.signal.aborted) return
    error.value = getApiError(cause, 'Не удалось сохранить или сформировать документ')
    if (axios.isAxiosError(cause)) {
      let body = cause.response?.data
      if (body instanceof Blob) {
        error.value = (await getBlobErrorMessage(body)) || error.value
        try {
          body = JSON.parse(await body.text())
        } catch {
          body = null
        }
      }
      for (const item of body?.errors || []) {
        if (typeof item.attr === 'string' && item.attr.startsWith('parameters.')) {
          errors.value[item.attr.slice('parameters.'.length)] = item.detail
        }
      }
      showFieldErrors()
    }
  } finally {
    if (!controller.signal.aborted) busy.value = false
  }
}
onMounted(() => {
  if (props.previewOnOpen) {
    previewVisible.value = true
    mobileTab.value = 'preview'
  }
  void prepare()
})
onBeforeUnmount(() => { request?.abort(); previewScheduler.cancel() })
</script>

<template>
  <VDialog
    model-value
    :z-index="2700"
    :max-width="previewVisible ? 1560 : 900"
    :height="smAndDown ? '100dvh' : 'min(900px, 92dvh)'"
    :fullscreen="smAndDown"
    scrollable
    :persistent="busy"
    @update:model-value="!$event && close()"
  >
    <VCard class="report-editor">
      <VCardTitle class="d-flex align-center justify-space-between flex-wrap gap-2 text-wrap flex-shrink-0">
        <span class="report-editor-title">{{ template.name }}</span>
        <VBtn
          variant="text"
          size="small"
          :prepend-icon="previewVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
          :aria-expanded="previewVisible"
          aria-controls="report-preview-pane"
          @click="previewVisible = !previewVisible"
        >{{ previewVisible ? 'Скрыть предпросмотр' : 'Показать предпросмотр' }}</VBtn>
        <VBtn
          icon="ri-close-line"
          variant="text"
          aria-label="Закрыть форму"
          :disabled="busy"
          @click="close"
        />
      </VCardTitle>
      <VTabs v-if="smAndDown && previewVisible" v-model="mobileTab" grow class="flex-grow-0 flex-shrink-0" aria-label="Режим заполнения">
        <VTab value="fields">Заполнение</VTab>
        <VTab value="preview">Предпросмотр</VTab>
      </VTabs>
      <div class="report-editor-body" :class="{ 'with-preview': previewVisible && !smAndDown }">
        <VCardText v-show="!smAndDown || !previewVisible || mobileTab === 'fields'" class="report-editor-fields">
          <VProgressLinear v-if="loading" indeterminate aria-label="Подготовка формы" />
          <div v-if="loading" class="py-4">
            Подготавливаем данные и поля…
          </div>
          <template v-if="prepared">
            <VAlert type="info" variant="tonal" class="mb-4">
              {{ prepared.selection.description }}<template v-if="prepared.source.id == null && prepared.source.ids?.length">
                . Состав зафиксирован на время заполнения.
              </template>
            </VAlert>
            <VTextField
              v-if="usesDraft"
              v-model="title"
              label="Название сохранённого документа"
              :maxlength="200"
              :disabled="busy"
              class="mb-2"
              hint="Черновик виден только вам. При повторной генерации используются актуальные сведения карточки."
              persistent-hint
            />
            <p v-if="!prepared.parameters.length" class="text-body-2 text-medium-emphasis mb-4">Для этой формы дополнительные поля не нужны. Выберите формат и скачайте документ.</p>
            <details v-if="prepared.source.id == null && prepared.selection.items.length" class="mb-5">
              <summary class="cursor-pointer">
                Посмотреть состав
              </summary>
              <ul class="mt-2 pl-5">
                <li v-for="item in prepared.selection.items" :key="item.id">
                  {{ item.label }}
                </li>
              </ul>
            </details>
            <VSelect
              v-if="template.wizard && groups.length > 1"
              v-model="step"
              :items="groups.map((title, value) => ({ title: `${value + 1}. ${title}`, value }))"
              label="Раздел формы"
              class="mb-4"
              :disabled="busy"
            />
            <section v-for="group in visibleGroups" :key="group" class="mb-6">
              <h3 class="text-subtitle-1 mb-3">
                {{ group }}
              </h3>
              <ReportParameterFields
                v-model="values"
                :fields="prepared.parameters.filter(item => item.group === group)"
                :errors="errors"
                :disabled="busy"
              />
            </section>
            <div v-if="template.wizard && groups.length > 1" class="d-flex align-center justify-space-between flex-wrap gap-2">
              <VBtn variant="text" :disabled="step === 0 || busy" @click="step--">
                Назад
              </VBtn>
              <span class="text-body-2">Раздел {{ step + 1 }} из {{ groups.length }}</span>
              <VBtn variant="tonal" :disabled="step >= groups.length - 1 || busy" @click="step++">
                Далее
              </VBtn>
            </div>
          </template>
        </VCardText>
        <ReportPreviewPanel
          v-if="previewVisible && (!smAndDown || mobileTab === 'preview')"
          id="report-preview-pane"
          :preview="preview"
          :loading="previewLoading"
          :error="previewError || (!prepared && !loading ? 'Сначала подготовьте данные формы на вкладке заполнения.' : '')"
          :format="format"
          @retry="updatePreview"
        />
      </div>
      <div v-if="error || notice" class="px-6 pb-3" aria-live="polite">
        <VAlert :type="error ? 'error' : 'success'" variant="tonal">
          {{ error || notice }}
        </VAlert>
        <VBtn
          v-if="!prepared && !loading"
          class="mt-2"
          variant="text"
          @click="prepare"
        >
          Повторить
        </VBtn>
      </div>
      <VCardActions class="px-6 pb-4 d-flex flex-wrap gap-2">
        <VSelect
          v-model="format"
          :items="template.formats.map(value => ({ value, title: labels[value] }))"
          label="Формат"
          hide-details
          density="compact"
          style="max-width: 160px"
          :disabled="busy"
        />
        <VSpacer />
        <VBtn v-if="usesDraft" variant="tonal" :disabled="!prepared || loading || busy" @click="perform(false)">
          Сохранить черновик
        </VBtn>
        <VBtn
          color="primary"
          variant="flat"
          :disabled="!prepared || loading"
          :loading="busy"
          @click="perform(true)"
        >
          {{ usesDraft ? 'Сохранить и скачать' : 'Скачать' }} {{ labels[format] }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
  <VDialog v-model="closeConfirm" :z-index="2800" max-width="440">
    <VCard title="Есть несохранённые изменения">
      <VCardText>Сохраните черновик, чтобы продолжить заполнение позже.</VCardText>
      <VCardActions class="flex-wrap">
        <VBtn @click="closeConfirm = false">
          Продолжить
        </VBtn>
        <VBtn @click="emit('close')">
          Закрыть без сохранения
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.report-editor { block-size: 100%; }
.report-editor-title { flex: 1 1 200px; }
.report-editor-body { display: grid; grid-template-columns: minmax(0, 1fr); min-block-size: 0; flex: 1 1 0; overflow: hidden; }
.report-editor-body.with-preview { grid-template-columns: minmax(360px, 0.85fr) minmax(0, 1.15fr); }
.report-editor-fields { min-inline-size: 0; overflow-y: auto; padding-block-start: 16px; }
.with-preview .report-editor-fields { border-inline-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); }
</style>
